/**
 * Map-scoped authority carried in sender-authenticated NaCl boxes.
 * This is recipient-verifiable authentication, not a public digital signature.
 * A recipient can forge a box to themselves, but cannot impersonate its sender
 * to any other recipient. User identities remain the existing X25519 keys.
 */
import nacl from 'tweetnacl';
import { Keypair } from './keypair.js';
import { t } from '../../shared/i18n/index.js';

const DOMAIN = 'orbital:map-key-authority:v1';
const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true });
const roles = new Set(['owner', 'admin', 'editor', 'viewer']);
const kinds = new Set(['bootstrap', 'invite', 'role', 'cancel', 'leave', 'rotate', 'rekey', 'supersede']);
/**
 * Kinds that can't change the map key. When a client can't verify one of
 * these, it keeps its last verified state and goes on (S-H1): a member could
 * otherwise post an event nobody else can open and pause sync for everyone.
 */
export const NON_KEY_KINDS = new Set(['leave', 'role', 'cancel', 'invite']);
// Shown to the user by the sharing and sync screens.
const fail = () => { throw new Error(t('sync.keyAuthority.unverified')); };

function canonical(value) {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.keys(value).sort().map(k => `${JSON.stringify(k)}:${canonical(value[k])}`).join(',')}}`;
  return JSON.stringify(value);
}

function validateState(state) {
  if (!state || state.domain !== DOMAIN || typeof state.docId !== 'string' ||
      typeof state.id !== 'string' || !kinds.has(state.kind) || !Number.isSafeInteger(state.epoch) || state.epoch < 0 ||
      !Array.isArray(state.members) || !state.members.length || state.members.length > 1000) fail();
  const ids = new Set();
  const keys = new Set();
  for (const member of state.members) {
    if (typeof member.userId !== 'string' || ids.has(member.userId) || !roles.has(member.role) ||
        Keypair.fromBase64(member.publicKey).length !== 32 || keys.has(member.publicKey)) fail();
    // The map's link (link-keys.js): an entry that is not an account, the
    // viewer or editor role it gives, never pending.
    if (member.link !== undefined && (member.link !== true || !['viewer', 'editor'].includes(member.role) || member.pending)) fail();
    ids.add(member.userId); keys.add(member.publicKey);
  }
  const owners = state.members.filter(m => m.role === 'owner');
  if (owners.length !== 1 || owners[0].userId !== state.ownerId) fail();
  if (!Array.isArray(state.witnesses) || state.witnesses.length > 1000) fail();
  const witnesses = new Map();
  for (const witness of state.witnesses) {
    if (witnesses.has(witness.userId) || Keypair.fromBase64(witness.publicKey).length !== 32) fail();
    witnesses.set(witness.userId, witness.publicKey);
  }
  for (const member of state.members) if (witnesses.get(member.userId) !== member.publicKey) fail();
  // `target` names the member whose identity a rekey replaces; nothing else carries it.
  if (state.kind === 'rekey' ? typeof state.target !== 'string' : state.target !== undefined) fail();
  // `base` names the verified state a supersede re-anchors on.
  if (state.kind === 'supersede' ? typeof state.base !== 'string' : state.base !== undefined) fail();
}

/**
 * Owner re-anchoring after events it could not verify (S-H1). A supersede
 * links to the server's head (`previous`, checked by followAuthority) but is
 * validated against the verifier's own last verified state, which may be
 * the owner's `base` or a later state other members did verify. Only the
 * owner may sign it; it can't replace an identity (every witness keeps its
 * pinned key) or change the owner entry, and — not being a rotation — it
 * must carry the same map key (checked in openAuthorityEvent).
 */
function validateSupersede(previous, next) {
  if (next.actorId !== previous.ownerId || next.epoch <= previous.epoch) fail();
  const known = new Map(previous.witnesses.map(w => [w.userId, w.publicKey]));
  const members = new Set(next.members.map(m => m.userId));
  for (const witness of next.witnesses) {
    if (known.has(witness.userId) ? known.get(witness.userId) !== witness.publicKey : !members.has(witness.userId)) fail();
  }
  const owner = next.members.find(m => m.userId === previous.ownerId);
  if (canonical(previous.members.find(m => m.userId === previous.ownerId)) !== canonical(owner)) fail();
}

/**
 * Owner-only restoration of one member's access under a replaced identity
 * key (after that user's password-confirmed identity repair). Everything but
 * the target's publicKey — members, roles, pending flags, other witnesses —
 * must be byte-identical, and the owner can't re-key themselves (recipients
 * pinned the owner key; that needs their own re-verification).
 */
function validateRekey(previous, next) {
  if (next.actorId !== previous.ownerId || next.target === previous.ownerId) fail();
  const before = new Map(previous.members.map(m => [m.userId, m]));
  if (!before.has(next.target) || next.members.length !== previous.members.length) fail();
  for (const member of next.members) {
    const old = before.get(member.userId);
    if (!old) fail();
    const expected = member.userId === next.target ? { ...old, publicKey: member.publicKey } : old;
    if (canonical(expected) !== canonical(member)) fail();
  }
  const oldWitnesses = new Map(previous.witnesses.map(w => [w.userId, w.publicKey]));
  if (next.witnesses.length !== previous.witnesses.length) fail();
  for (const witness of next.witnesses) {
    if (!oldWitnesses.has(witness.userId)) fail();
    if (witness.userId !== next.target && oldWitnesses.get(witness.userId) !== witness.publicKey) fail();
  }
}

/**
 * Where a client whose identity key changed can (re)start verifying a map's
 * history: the first event whose package is addressed to its current key.
 * Earlier events are sealed to the replaced key and can't be opened. A client
 * that had already pinned this map (`requireOwnerRekey`) only accepts an
 * owner-signed rekey naming it, so its pinned owner vouches for the new key.
 * @returns {number} index into `events`, or -1
 */
export function enrollmentStart(events, userId, publicKey, { requireOwnerRekey = false } = {}) {
  return events.findIndex(event => {
    const state = event?.state;
    if (state?.witnesses?.find?.(w => w.userId === userId)?.publicKey !== publicKey) return false;
    return !requireOwnerRekey ||
      (state.kind === 'rekey' && state.target === userId && state.actorId === state.ownerId);
  });
}

/** Validate against the PREVIOUS authority, never a role asserted by the event. */
export function validateTransition(previous, next) {
  validateState(next);
  if (!previous) {
    if (next.epoch !== 0 || next.previous !== null || next.actorId !== next.ownerId || next.kind !== 'bootstrap') fail();
    return;
  }
  validateState(previous);
  if (next.docId !== previous.docId || next.ownerId !== previous.ownerId) fail();
  if (next.kind === 'supersede') return validateSupersede(previous, next);
  if (next.previous !== previous.id || next.epoch !== previous.epoch + 1) fail();
  if (next.kind === 'rekey') return validateRekey(previous, next);
  const actor = previous.members.find(m => m.userId === next.actorId);
  // A link never acts in the key log: whoever holds it reads only.
  if (!actor || actor.link) fail();
  const before = new Map(previous.members.map(m => [m.userId, m]));
  const after = new Map(next.members.map(m => [m.userId, m]));
  const witnesses = new Map(next.witnesses.map(m => [m.userId, m.publicKey]));
  for (const witness of previous.witnesses) {
    if (witnesses.get(witness.userId) !== witness.publicKey) fail();
  }
  for (const witness of next.witnesses) {
    if (!previous.witnesses.some(w => w.userId === witness.userId) &&
        after.get(witness.userId)?.publicKey !== witness.publicKey) fail();
  }
  if (canonical(before.get(previous.ownerId)) !== canonical(after.get(previous.ownerId))) fail();
  for (const member of next.members) {
    const old = before.get(member.userId);
    // Identity replacement is deliberately unsupported, including by owners.
    if (old && old.publicKey !== member.publicKey) fail();
    if (canonical(old) === canonical(member)) continue;
    // The map's link (an entry marked `link`): only the owner adds one (an
    // `invite`, exactly its four fields) or changes its role (a `role`,
    // viewer or editor, its key and mark kept), as only the owner removes
    // people; a member never becomes a link nor a link a member. As the
    // server (key_authority.rs).
    if (old?.link || member.link) {
      if (actor.role !== 'owner') fail();
      if (old && (old.link !== true || member.link !== true)) fail();
      const fields = Object.keys(member);
      if (fields.length !== 4 || !['userId', 'role', 'publicKey', 'link'].every(k => fields.includes(k))) fail();
      if (!old && next.kind !== 'invite') fail();
      if (old && next.kind !== 'role') fail();
      continue;
    }
    if (actor.role === 'owner') continue;
    if (actor.role === 'admin' && !['owner', 'admin'].includes(member.role) &&
        (!old || !['owner', 'admin'].includes(old.role))) continue;
    if (next.kind === 'role' && actor.role === 'editor' && old &&
        ['viewer', 'editor'].includes(old.role) && ['viewer', 'editor'].includes(member.role)) continue;
    fail();
  }
  for (const member of previous.members) {
    if (after.has(member.userId)) continue;
    if (actor.role === 'owner') continue;
    if (next.kind === 'leave' && member.userId === actor.userId) continue;
    if (['cancel', 'rotate'].includes(next.kind) && actor.role === 'admin' && member.pending && !['owner', 'admin'].includes(member.role)) continue;
    fail();
  }
  if (!['owner', 'admin'].includes(actor.role) && !['role', 'leave'].includes(next.kind)) fail();
  if (next.kind === 'leave' && (after.has(actor.userId) ||
      canonical(next.members) !== canonical(previous.members.filter(m => m.userId !== actor.userId)))) fail();
}

/**
 * @param {Object} [args.chain] - supersede only: `{id, epoch}` of the server's
 *   head, which the event links to; `previous` is then the owner's last
 *   verified state (the `base`), which it is validated against.
 */
export function createAuthorityEvent({ docId, previous = null, members, ownerId, actorId, kind, dek, keypair, target, chain = null }) {
  if ((kind === 'supersede') !== !!chain || (chain && !previous)) fail();
  const witnesses = new Map((previous?.witnesses || []).map(m => [m.userId, m]));
  for (const member of members) {
    const replacing = kind === 'rekey' && member.userId === target;
    if (!replacing && witnesses.has(member.userId) && witnesses.get(member.userId).publicKey !== member.publicKey) fail();
    witnesses.set(member.userId, { userId: member.userId, publicKey: member.publicKey });
  }
  const link = chain || previous;
  const state = {
    domain: DOMAIN, docId, id: crypto.randomUUID(), epoch: link ? link.epoch + 1 : 0,
    previous: link?.id || null, ownerId, actorId, kind,
    ...(kind === 'rekey' && { target }),
    ...(kind === 'supersede' && { base: previous.id }),
    members: members.map(m => ({ ...m })).sort((a, b) => a.userId.localeCompare(b.userId)),
    witnesses: [...witnesses.values()].sort((a, b) => a.userId.localeCompare(b.userId)),
  };
  validateTransition(previous, state);
  const actor = (previous || state).members.find(m => m.userId === actorId);
  if (!actor || actor.publicKey !== Keypair.toBase64(keypair.publicKey) || dek.length !== 32) fail();
  const stateHash = Keypair.toBase64(nacl.hash(encoder.encode(canonical(state))));
  const memberIds = new Set(state.members.map(m => m.userId));
  const packages = Object.create(null);
  for (const member of state.witnesses) {
    // Former members can verify the authority chain if re-invited later, but
    // receive no future DEK. Hash binding keeps packages constant-sized.
    const plaintext = encoder.encode(canonical({ stateHash,
      dek: memberIds.has(member.userId) ? Keypair.toBase64(dek) : null }));
    const nonce = nacl.randomBytes(nacl.box.nonceLength);
    const ciphertext = nacl.box(plaintext, nonce, Keypair.fromBase64(member.publicKey), keypair.secretKey);
    const bytes = new Uint8Array(nonce.length + ciphertext.length);
    bytes.set(nonce); bytes.set(ciphertext, nonce.length);
    packages[member.userId] = Keypair.toBase64(bytes);
    plaintext.fill(0);
  }
  return { state, packages };
}

/**
 * A root identity must already be trusted (self for owners; explicit first
 * enrollment for recipients). Established clients must supply previous state.
 */
export function openAuthorityEvent(event, { docId, userId, keypair, previous = null, previousDek = null, ownerPublicKey, enrollment = false }) {
  const state = event?.state;
  validateState(state);
  if (state.docId !== docId) fail();
  const owner = state.members.find(m => m.role === 'owner');
  if (!ownerPublicKey || owner.publicKey !== ownerPublicKey) fail();
  if (previous || !enrollment) validateTransition(previous, state);
  const actor = (previous || state).members.find(m => m.userId === state.actorId);
  if (!actor || (!previous && !['owner', 'admin'].includes(actor.role))) fail();
  const recipient = state.members.find(m => m.userId === userId);
  const witness = state.witnesses.find(m => m.userId === userId);
  if (!witness || witness.publicKey !== Keypair.toBase64(keypair.publicKey)) fail();
  const raw = Keypair.fromBase64(event.packages?.[userId] || '');
  if (raw.length < 40 || raw.length > 1024 * 1024) fail();
  const plaintext = nacl.box.open(raw.subarray(24), raw.subarray(0, 24), Keypair.fromBase64(actor.publicKey), keypair.secretKey);
  if (!plaintext) fail();
  try {
    const content = JSON.parse(decoder.decode(plaintext));
    if (content.stateHash !== Keypair.toBase64(nacl.hash(encoder.encode(canonical(state))))) fail();
    if (!recipient) {
      if (content.dek !== null) fail();
      return { state, dek: null };
    }
    const dek = Keypair.fromBase64(content.dek);
    if (dek.length !== 32) fail();
    // Editors may change viewer/editor roles and readers may leave. Neither
    // operation is authority to replace the map key. Even an authenticated
    // package must keep the established DEK for non-rotation transitions.
    // The one exception is the target of an owner rekey: its replaced key
    // can't open the old packages, so it may not hold the current DEK.
    const rekeyedHere = state.kind === 'rekey' && state.target === userId;
    if (previous?.members.some(m => m.userId === userId) && state.kind !== 'rotate' && !rekeyedHere &&
        (!previousDek || !nacl.verify(dek, previousDek))) { dek.fill(0); fail(); }
    if (rekeyedHere && previousDek && !nacl.verify(dek, previousDek)) { dek.fill(0); fail(); }
    return { state, dek };
  } finally { plaintext.fill(0); }
}

/**
 * Walk a map's authority history from the last verified `state` (null for a
 * first enrollment). Verified events advance the state and the map key.
 *
 * S-H1: an event that can't change the map key (NON_KEY_KINDS) and fails
 * verification — unopenable package, forged roster, or built on another
 * such event — is recorded in `skipped` and ignored: the client keeps its
 * last verified state and key, and sync goes on. Anything else that fails
 * still throws (the caller pauses the map's sync). An owner `supersede`
 * re-anchors everyone after skipped events; it must link to the last event
 * walked, verified or not.
 *
 * @returns {{state, dek, skipped: Array<{id, epoch, kind, actorId}>, head: {id, epoch}|null}}
 */
export function followAuthority(events, { docId, userId, keypair, state = null, dek = null, ownerPublicKey, enrollment = false, onVerified = null }) {
  let head = state ? { id: state.id, epoch: state.epoch } : null;
  const skipped = [];
  for (const event of events) {
    const next = event?.state;
    if (head && next?.epoch <= head.epoch) continue;
    let result;
    try {
      if (state && next?.kind === 'supersede' && (next.previous !== head.id || next.epoch !== head.epoch + 1)) fail();
      result = openAuthorityEvent(event, { docId, userId, keypair, previous: state, previousDek: dek,
        ownerPublicKey, enrollment: !state && enrollment });
    } catch (error) {
      const linked = head && Number.isSafeInteger(next?.epoch) && next.epoch === head.epoch + 1 &&
        typeof next?.id === 'string' && next.docId === docId;
      if (!state || !linked || !NON_KEY_KINDS.has(next?.kind)) { dek?.fill(0); throw error; }
      skipped.push({ id: next.id, epoch: next.epoch, kind: next.kind, actorId: next.actorId });
      head = { id: next.id, epoch: next.epoch };
      continue;
    }
    onVerified?.(result);
    if (dek && dek !== result.dek) dek.fill(0);
    ({ state, dek } = result);
    head = { id: state.id, epoch: state.epoch };
    // The owner re-anchored: what we skipped before is resolved.
    if (state.kind === 'supersede') skipped.length = 0;
  }
  return { state, dek, skipped, head };
}
